/**
 * The one seeded pseudo-random generator.
 *
 * Nothing in the simulation may call the platform's own `Math.random`: a run that cannot be
 * repeated cannot be compared against the original game, and the fidelity trace is worthless
 * without that. Every source of chance is therefore either a stream from here, started from a
 * recorded seed, or a value derived from the tick number and the player's id.
 *
 * The algorithm is mulberry32 — 32 bits of state, one multiply-shift round. It is not
 * cryptographic and does not need to be; it needs to be identical on every machine, which a
 * pure 32-bit integer generator is and a floating-point one is not.
 */

/** A stream of numbers in [0, 1), repeatable for a given seed. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fold two seeds into one, so a per-player or per-team stream can be derived from the run's
 * single seed without two players ever sharing a stream.
 */
export function mixSeed(seed: number, salt: number): number {
  let value = (seed ^ Math.imul(salt, 0x9e3779b1)) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x21f0aaad) >>> 0;
  return (value ^ (value >>> 15)) >>> 0;
}

/**
 * A number in [0, 1) derived from where and who rather than from a running stream.
 *
 * A stream has to be consumed in a fixed order to repeat, which makes it fragile wherever the
 * number of draws depends on what happened this tick. Hashing the tick, the player and a salt
 * has no such requirement: the same tick and the same player always give the same answer, and
 * adding a draw somewhere else moves nothing. The AI in `controller.ts` folds the run's seed in
 * exactly this way for the same reason.
 *
 * TWO DRAWS THAT DIFFER ONLY IN THEIR SALT MUST BE INDEPENDENT, AND ONCE THEY WERE NOT. The
 * first version of this xor-ed three multiplied terms and finished with a single `value ^=
 * value >>> 16`, which only folds the top sixteen bits down into the bottom sixteen and leaves
 * the top sixteen — the ones that decide the returned value — exactly as the raw sum left them.
 * The salt entered as `id + salt * 97` inside one of those terms, so changing it shifted the
 * result by a fixed amount rather than reshuffling it.
 *
 * Each draw on its own looked perfect and every test of one passed. What broke was asking a
 * SECOND question of the same player on the same tick, having already conditioned on the first:
 * an injury is drawn only when its chance roll lands in the lowest 2% of the range, and across
 * those the severity roll came out 86% / 0% / 14% against the 60% / 30% / 10% it was written to
 * be. **The middle severity was not rare, it was unreachable** — 69 injuries over 48 measured
 * matches and not one of them, which is what made it visible at all.
 *
 * So the salt now gets a multiply of its own and the result goes through murmur3's finaliser,
 * which avalanches every input bit through every output bit. Measured after the change over
 * 8.8 million draws, conditioned the same way: 60.0% / 30.0% / 10.0%.
 */
export function hashedRandom(tick: number, id: number, salt: number, seed: number): number {
  let value =
    Math.imul(tick | 0, 0x45d9f3b) ^
    Math.imul(id | 0, 0x27d4eb2d) ^
    Math.imul(salt | 0, 0x85ebca6b) ^
    Math.imul(seed, 0x9e3779b1);
  // murmur3's fmix32. Three rounds, so one input bit changing flips about half the output bits.
  value ^= value >>> 16;
  value = Math.imul(value, 0x85ebca6b);
  value ^= value >>> 13;
  value = Math.imul(value, 0xc2b2ae35);
  value ^= value >>> 16;
  return (value >>> 0) / 0x100000000;
}
